<script setup lang="ts">
import { ref } from 'vue';
import { PSOShort, Days } from '../constants';
import type { TOUProfileMini } from '../types';

const props = defineProps<{
  season: string;
  data: TOUProfileMini[];
  activeState: string;
  headerHidden?: boolean;
}>();

const emit = defineEmits<{
  (e: 'update:data', val: TOUProfileMini[]): void;
}>();

const rows = [
  { key: Days.Weekdays, label: Days.Weekdays },
  { key: Days.Saturday, label: Days.Saturday },
  { key: Days.Sunday, label: Days.Sunday }
];

const hours = Array.from({ length: 24 }, (_, h) => h);
const hovered = ref<[number, number]>([-1, -1]);

const getDayField = (day: Days.Weekdays | Days.Saturday | Days.Sunday) => {
  const fieldMap = {
    Weekdays: 'Week',
    Saturday: 'Saturday',
    Sunday: 'Sunday'
  } as const;
  return fieldMap[day];
};

function getCellClass(day: Days.Weekdays | Days.Saturday | Days.Sunday, hour: number) {
  const profile = props.data.find(p => p.Hour === hour);
  if (!profile) return {};

  const field = getDayField(day);
  const state = profile[field].trim();

  return {
    'cell-peak': state === PSOShort.Peak,
    'cell-standard': state === PSOShort.Standard,
    'cell-offpeak': state === PSOShort.OffPeak,
  };
}


function toggleCell(day: Days.Weekdays | Days.Saturday | Days.Sunday, hour: number) {
  const field = getDayField(day);
  const newData = props.data.map(entry => {

    if (entry.Hour !== hour) return entry;
    return {
      ...entry,
      [field]: props.activeState,
    };
  });
  emit('update:data', newData);
}

</script>

<template>
  <div class="w-100">
    <table class="tou-profile-table" @mouseout="hovered = [-1, -1]">
      <thead>
        <tr>
          <th colspan="2"></th>
          <th v-if="!props.headerHidden" :colspan="hours.length" class="days-legend headers" >Hours</th>
        </tr>
        <tr>
          <th colspan="2"></th>
          <th v-for="hour in hours" :key="hour" class="day">
            {{ hour.toString().padStart(2, '0') }}:00
          </th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(row, dayIndex) in rows" :key="row.label">
          <th v-if="dayIndex === 0" :rowspan="rows.length" class="season-legend">
            <span>{{ props.season }}</span>
          </th>
          <th class="hour">{{ row.label }}</th>
          <td
            v-for="hour in hours"
            :key="`${row.key}:${hour}`"
            @mouseover="hovered = [dayIndex, hour]"
            @click="toggleCell(row.key, hour)"
            :class="[
              getCellClass(row.key, hour),
              {
                line:
                  dayIndex === hovered[0] && hour <= hovered[1] ||
                  dayIndex <= hovered[0] && hour === hovered[1]
              }
            ]"
          ></td>
        </tr>
      </tbody>
    </table>
  </div>
</template>


<style scoped>

.tou-profile-table td {
  border: 1px solid #D1D1D1;
  width: 24px;
  height: 24px;
  cursor: pointer;
}

.tou-profile-table th.days-legend {
  text-align: center;
}
.tou-profile-table th.season-legend span {
  margin-right: 3px;
  transform: rotate(180deg);
  writing-mode: vertical-lr;
  font-weight: bolder;
}
.tou-profile-table th {
  border: none;
  font-weight: var(--bs-body-font-weight);
  padding: 0.25rem 0.5rem;
}
.tou-profile-table td.line {
  background: rgba(37, 149, 190, 0.1);
}

.cell-peak {
  background:#FD562480!important;
}
.cell-standard {
  background:#41C8DB80!important;
}
.cell-offpeak {
  background:#103a4880!important;
}
.headers{
  font-weight: bolder!important;
}
</style>
